define([
	"dojo/_base/declare",
	"dojo/has",
	"csi",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/constants",
	"csi/core/extendedPathService",
	"csi/reflection",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryProcessor",
	"./_RequestQueryMixin",
	"./Chart",
	"./QueryEngine",
	"./QueryResults",
	"./Request",
	"./Values",
	"./ValuePairs",
	"./ValuePairsFromNodes"
], function(declare, has, csi, _, csiCrews, comm, PostRequest, constants, {convertToStandardPath}, reflection, xml, Query, QueryProcessor,
	_RequestQueryMixin, Chart, QueryEngine, QueryResults, RequestStore, Values, ValuePairs, ValuePairsFromNodes) {

	var MAX_QUERY_BY_URL = 50;

	/**
	 * Apply query to given set of URLs and return list of URLs that satisfy query.
	 * @param {Query} clientQuery - Query to apply
	 * @param {string[]} urls - Candidate set of URLs to which query is applied
	 * @param {Cache} store - Cache containing nodes for URLs
	 * @return {string[]} List of URLs matching query.
	 */
	function _applyClientQuery(clientQuery, urls, store) {
		var nodes = _getAllNodes(urls, store);
		if (nodes) {
			return _.map(QueryProcessor.applyQueryToNodes(clientQuery, nodes, store), xml.nodeGetURL, xml);
		}

		return urls;
	}

	/**
	 * Return true if should issue nodes request (QueryByURL) rather than regular request (QueryByXML)
	 * @param {Request} nodesRequest
	 * @param {Query} q
	 * @paran {number} end
	 * @return {boolean}
	 */
	function _canUseNodesRequest(nodesRequest, q, end) {
		var urls = nodesRequest.getParameter(comm.QUERY_URL);
		var n = urls ? urls.length : 0;

		// if no query, then query by url if number of urls is less than page end
		if (!q || q.isEmpty()) {
			return !end || (n <= end);
		}

		// if there is a query, then we don't want to get many more nodes than we need;
		// so, use QueryByXML so query will be applied on server
		return n <= Math.min(end || MAX_QUERY_BY_URL, MAX_QUERY_BY_URL);
	}

	/**
	 * Extract nodes from cache for given urls at the given timestamp (if specified); if one is missing, then return null
	 * @param {string[]} urls - Candidate set of URLs to which query is applied
	 * @param {Cache} store - Cache containing nodes for URLs
	 * @param {number} [tm] - timstamp
	 * @return {Node[]}
	 */
	function _getAllNodes(urls, store, tm) {
		var nodes = [];
		var bAll = _.every(urls, function(url) {
			var node = store.getByTime ? store.getByTime(url, tm) : store.get(url);
			nodes.push(node);
			return !!node;
		});

		return bAll ? nodes : null;
	}

	/**
	 * Determine urls that exist in given store.
	 * @return {string[]} List of URLs whose Node exists in store
	 */
	function _getResolvedUrls(/*Store*/store, /*String[]*/urls) {
		return _.filter(urls, function(url) {
			return !!store.get(url);
		});
	}

	function _getStartCount(options) {
		return (options.start || 0) + 1;
	}

	function _getEndCount(options) {
		return options.count ? ((options.start || 0) + options.count) : 0;
	}

	function _getStartEndRange(a, start, end) {
		if (start && end) {
			a = a.slice(start - 1, end);
		} else if (start > 1) {
			a = a.slice(start - 1);
		}

		return a;
	}

	/**
	 * If existing query is a subset of the new query, then return only the difference
	 * @param {Object} newQuery
	 * @param {Object} existingQuery
	 * @return {Object}
	 */
	function _queryDifference(newQuery, existingQuery) {
		if (_.isEmpty(newQuery) || _.isEmpty(existingQuery)) {
			return newQuery;
		}

		const resultQuery = _.clone(newQuery);
		const match = _.every(existingQuery, function(value, key) {
			const value2 = newQuery[key];

			if (_.isEqual(value, value2)) {
				delete resultQuery[key];
				return true;
			}

			return false;
		});

		return match ? resultQuery : newQuery;
	}

	var _Nodes = declare([RequestStore, _RequestQueryMixin], {
		chartClass: Chart,
		queryClass: Query,
		queryEngine: QueryEngine,
		valuesClass: Values,
		valuePairsClass: ValuePairs,
		requestBinaryParameter: comm.QUERY_XML,
		requestModule: comm.MOD_SEARCH,
		requestOperation: comm.OP_QUERY,
		requestOperationCount: "CountByXML",
		requestOmitOptions: ["count", "direction", "noActionData", "queryOptions", "start", "store"],

		// Boolean: if true, new items will be added to front of list
		//bAddToFront: false,

		// Boolean: if true, then client will track when it has all results and apply filtering on client rather than requesting from server
		bTrackAllResults: !has("csi-no-store-caching"),

		// Boolean: if true, then the number of results in the view exceeds the count threshold
		//bQueryCountLimited: false,

		// Boolean: if true, request will only be issued if a query filter is applied - this is useful for querying large data sets
		// where the first page of all results is meaningless
		//bQueryRequired: false,

		// Store: store where to cache items - resultUrls tracks the actual query results
		//cachingStore: null,

		// Boolean: if true, query using Or rather than the default And
		//queryByAny: false,

		// Number: timestamp to determine if data is stale and needs to be refreshed
		//refreshTimestamp: 0,

		// String: module to use when querying for individual nodes
		requestNodeModule: comm.MOD_SEARCH,

		// String: operation to use when querying for individual nodes
		requestNodeOperation: comm.OP_URL,

		// Object: parameters to omit when just querying for individual nodes with requestNodeOperation
		requestNodeOmitParameters: constants.REQUEST_URL_OMIT_PARAMS,

		// String[]: full list of result urls, even if we don't have all data cached
		//resultUrls: null,

		// String: property to use to get node URL
		urlProperty: "$URL",

		// String[]: full list of result urls when no ad-hoc query
		//_allResultUrls: null,

		// Boolean: if store data is initialized by default, then do not create request to server for data
		//_bSuppressRequest: false,

		// Number: number of results total - could be more than length of resultUrls if that was capped at some limit
		//_total: null,

		constructor: function(/*Object*/options) {
			// summary: if urls are known, then set store as loaded and determine total count
			if (this.resultUrls) {
				this.bTrackAllResults = true;
				this._bSuppressRequest = true;
				this._loaded = true;
				this._total = this.resultUrls.length;
			}
		},

		add: function(object, directives) {
			// summary: this function overrides the default behavior to support the following use case:
			// if store is attached to cache and object is already in cache, then adding to this store
			// will throw exception even if object is not in this store; so honor overwrite flag if present;
			// also, check for uniqueness before inserting again
			const {bAddToFront, idProperty, resultUrls, _allResultUrls, _store} = this;
			let id = object[idProperty];

			if (id == null) {
				id = object[idProperty] = xml.getClientNodeUrl();
			}

			// check for uniqueness
			if (resultUrls && (_.indexOf(resultUrls, id) >= 0)) {
				throw new Error("Object already exists");
			}

			const insert = (urls) => {
				const n = (directives && directives.before) ? _.indexOf(urls, directives.before) : (bAddToFront ? 0 : -1);
				if (n >= 0) {
					urls.splice(n, 0, id);
				} else {
					urls.push(id);
				}
			};

			// insert into url list
			if (resultUrls) {
				insert(resultUrls);
				++this._total;
			}

			// add to full list as well
			if (_allResultUrls) {
				insert(_allResultUrls);
			}

			_store.add(object, directives);

			return id;
		},

		createNodesRequest: function(/*String[]*/urls, /*Object?*/options, /*Object*/extraPaths) {
			// summary: generate request for missing urls
			var store = this._store;
			var params;

			if (!urls.length || !this.requestNodeModule || !this.requestNodeOperation) {
				return null;
			}

			// check if nodes are already in cache if just simple memory store
			if (!store.reduceNodesRequest) {
				urls = _.filter(urls, function(url) {
					return !store.get(url);
				});

				if (!urls.length) {
					return null;
				}
			}

			params = {};
			params[comm.QUERY_URL] = urls;

			if (options && options.requestParameters) {
				_.extend(params, options.requestParameters);
			}

			if (this.requestParameters) {
				_.extend(params, this.requestParameters);
			}

			if (this.requestNodeOmitParameters) {
				params = _.omit(params, this.requestNodeOmitParameters);
			}

			var request = new PostRequest(this.requestNodeModule, this.requestNodeOperation, params);

			// add standard search parameters
			request.addNodeCrewParameters();

			// add extra dependency paths if specified - make sure to use standard paths
			if (extraPaths) {
				request.addDependencyPaths(csi.transform(extraPaths, convertToStandardPath));
			}

			// check if nodes are already in cache
			if (store.reduceNodesRequest) {
				request = store.reduceNodesRequest(request, this.refreshTimestamp);
			}

			return request;
		},

		getAllResults: function(/*Object?*/query, /*Object?*/options,) {
			// summary: return result nodes that satisfy given query if all results are cached
			var nodes;
			var urls = this._allResultUrls;
			if (urls) {
				nodes = _getAllNodes(urls, this._store);

				if (nodes && query) {
					var q = this._getClientCacheQuery(query, options);
					nodes = QueryProcessor.applyQueryToNodes(q, nodes, this._store);
				}
			}

			return nodes;
		},

		getValuePairsStore: function(/*Spec*/spec1, /*Spec*/spec2, /*Object?*/storeOptions) {
			// summary: optimization - if this store is created in memory, then create values store from nodes in memory
			if (this._bSuppressRequest) {
				var options = _.extend({
					spec1: spec1,
					spec2: spec2
				}, storeOptions);

				return new ValuePairsFromNodes({
					_store: this,
					spec1: spec1,
					spec2: spec2,
					valuesStoreOptions: options
				});
			}

			return this.inherited(arguments);
		},

		_createQueryRequest: function(/*Object?*/query, /*Object?*/options) {
			// summary: generate server request for given query and options
			if (this.bQueryRequired && _.isEmpty(query)) {
				this.resultUrls = null;
				this._lastQuery = null;
				this._lastOptions = null;
				this._total = null;
				return null;
			}

			var request;

			if (this._bSuppressRequest) {
				// track _lastQuery and _lastOptions to support getFullRequest and getReportRequest
				this._lastQuery = this._getServerQuery(query);
				this._lastOptions = this._getServerOptions(options);

				// track if results are sorted
				var sortQuery = this._getSortQueryFromOptions(this._lastOptions);
				if (sortQuery && !sortQuery.isEmpty()) {
					this.bSorted = true;
				}

				// track if results are filtered - we are not performing query as the results are already known, but need to know
				// if they are not the full set of results
				var filterQuery = this._getFilterQueryFromQuery(this._lastQuery);
				if (filterQuery && !filterQuery.isEmpty()) {
					this.bFiltered = true;
				}
			} else {
				// generate request for store based on query and options
				request = this.getRequest(query, options);
			}

			var counts = this._getStartEndCounts(options);
			var start = counts[0];
			var end = counts[1];

			if (request) {
				if (start == 1) {
					// reset members when new request starting at 1
					this._unload();

					// determine if client can satisfy the request, or potentially reduce to a faster request by url
					var urls = this._allResultUrls;
					if (urls) {
						// all nodes cached, so set urls with new query now; the query is just filtering down the list
						// of known urls and should not require and new nodes/paths. Previously we still called createNodesRequest
						// in this case, but that can cause issues with users who do not have full access to everything in the view,
						// such as hybrid users. Instead, we assume that if we have queried for all nodes in the view, then
						// we have everything we need to filter the results further. If we don't, then that means the user does
						// not have access to that information and they should not see it anyways.
						request = null;
						this._setUrls(urls, this._getClientCacheQuery(this._lastQuery, this._lastOptions));
					} else if (urls = this._resolveLimitPathUrls(request)) {
						// determine what nodes and paths still need to be requested to fulfill limit path
						const {binaryQuery} = request;
						const nodesRequest = this.createNodesRequest(urls, options, binaryQuery?.getPaths());

						if (!nodesRequest) {
							// all nodes cached, so set urls now
							request = null;
							this._setUrls(urls, binaryQuery);
						} else if (_canUseNodesRequest(nodesRequest, binaryQuery, end)) {
							// the urls are known and within limit, so request by URL rather than XML
							request = nodesRequest;
							request.requestNodesQuery = binaryQuery;
							request.requestNodesUrls = urls;
						} else {
							// performing new query because parameters have changed, so ask for first page and all result urls up to end
						}
					}
				}
			} else if (this._loaded) {
				// generate request for any missing nodes based on paging
				request = this.createNodesRequest(_getStartEndRange(this.resultUrls, start, end), options);
			}

			return request;
		},

		_getStartEndCounts: function(/*Object?*/options) {
			// summary: return start/end parameters of the query based on options

			// determine nodes to query - if no page size, then still cap search at max
			var start = options ? _getStartCount(options) : 1;
			var end = options ? _getEndCount(options) : 0;
			var max = this.requestParameters && this.requestParameters["Fmt.Complete.Max"];

			if (max && !end) {
				end = max;
			}

			return [start, end];
		},

		getRequest: function(/*Object?*/query, /*Object?*/options, /*Boolean?*/bReport, /*Boolean?*/bForce) {
			// summary: generate server request for given query and options, or override and just return any Deferred
			var request = this.inherited(arguments);
			if (request == null) {
				return null;
			}

			var counts = this._getStartEndCounts(options);
			var start = counts[0];
			var end = counts[1];

			if (start && end) {
				request.addParameter("Qry.Limit.Begin", start);
				request.addParameter("Qry.Limit.End", end);

				if ((start == 1) && !bForce) {
					request.addParameter("Fmt.Complete", "Ref");
				}
			}

			if (!bReport) {
				request.addNodeCrewParameters();
			}

			return request;
		},

		getFullRequest: function() {
			// summary: issue request for full set of urls if previously limited by Fmt.Complete.Max
			if (this.isLoaded() && this.resultUrls && (this.getSize() == this.getTotal())) {
				return this.resultUrls;
			}

			return this.getRequest(this._lastQuery, this._lastOptions)
				.removeParameter("Fmt.Complete.Max")
				.addParameter("Fmt.Complete", "Ref")
				.addParameter("Fmt.Attr.Inc", "No")
				.addParameter("Fmt.Node.Info", "Min")
				.addParameter("Qry.Limit.Begin", 1)
				.addParameter("Qry.Limit.End", 1)
				.go()
				.then(function(res) {
					return res.getCompleteRefs();
				});
		},

		getReportRequest: function() {
			// summary: return request necessary for reporting
			return this.getRequest(this._lastQuery, this._lastOptions, true);
		},

		/**
		 * Return list of result nodes that have been loaded in store
		 * @return {Node[]}
		 */
		getResultNodes: function() {
			var nodes = [];
			var store = this._store;

			_.every(this.resultUrls, function(url) {
				var node = store.get(url);
				if (node) {
					nodes.push(node);
					return true;
				}

				return false;
			});

			return nodes;
		},

		getResults: function() {
			// summary: return list of result urls
			return this.resultUrls;
		},

		getResultIndex: function(/*Node*/item) {
			// summary: return index of result in list
			return _.indexOf(this.resultUrls, item.$URL);
		},

		getResultTotal: function(/*Number?*/nDisplayed) {
			// summary: return total number of results in store or number displayed - needed to provide common API across group and non-group stores
			return nDisplayed || this._total;
		},

		getSize: function() {
			// summary: return number of items in store - may be less than getTotal if number of items has max limit
			return this.resultUrls ? this.resultUrls.length : 0;
		},

		getTotal: function() {
			// summary: return total number of results from query
			return this._total;
		},

		remove: function(id) {
			// summary: remove id from result list - do not remove from caching store though (this is not "delete");
			// return index of url in result list
			var n = this.resultUrls ? _.indexOf(this.resultUrls, id) : -1;
			if (n >= 0) {
				this.resultUrls.splice(n, 1);
				--this._total;
			}

			// also remove from full list
			if (this._allResultUrls) {
				n = _.indexOf(this._allResultUrls, id);
				if (n >= 0) {
					this._allResultUrls.splice(n, 1);
				}
			}

			return n;
		},

		/**
		 * Extension point for when all results are know
		 * @param {cnl} resultUrls 
		 */
		onResultUrls(resultUrls) { },

		reset: function() {
			// summary: save timestamp when store was reset to make sure stale data is not reused
			this.resetTimestamp();
			this._allResultUrls = null;
			this.inherited(arguments);
		},

		resetTimestamp: function() {
			// summary: save timestamp when store was reset to make sure stale data is not reused
			this.refreshTimestamp = new Date().getTime();
		},

		_doQuery: function(query, options) {
			// summary: query data to get final results; by the time this function is called, either the query to the
			// server will be complete, or there was no query at all in which case the store is empty
			var urls = this.resultUrls = this.resultUrls || [];
			var nodes;

			if (this.bSimpleQueryEngine) {
				// apply query to all nodes in store using normal store query engine
				nodes = this._getNodes(urls);
				nodes = this.queryEngine(query, options)(nodes);
			} else {
				const start = options && _getStartCount(options);
				const end = options && _getEndCount(options);
				let total = this._total;

				// apply query to all nodes in store using QueryProcessor if using cache; otherwise server would already have applied query
				const bCache = this._bSuppressRequest || (options && options.useCache);
				if (bCache) {
					// simplify query based on last query since we are querying based on resultUrls (that are already filtered by _lastQuery)
					query = _queryDifference(query, this._lastQuery);
					urls = _applyClientQuery(this._getClientCacheQuery(query, options), urls, this._store);
					total = urls.length;
				}

				// apply paging to nodes in store, but assign correct total for all results regardless of page count
				urls = _getStartEndRange(urls, start, end);
				nodes = this._getNodes(urls);

				// in some cases the number of nodes returned will not equal the number queried, such as if the node
				// is deleted or the user no longer has access to it; the targetCount is used to detect such cases by
				// comparing to the number of results returned
				nodes.targetCount = urls.length;

				// update total based on difference between urls requested and nodes receieved
				nodes.total = total - (nodes.targetCount - nodes.length);
			}

			return QueryResults(nodes);
		},

		_getByTime: function(/*String*/id, /*Number?*/tm) {
			// summary: retrieve node if newer than given timestamp (if store supports it)
			var store = this._store;
			return store.getByTime ? store.getByTime(id, tm) : store.get(id);
		},

		_getNodes: function(/*String[]*/urls, /*Number?*/tm) {
			// summary: retrieve nodes from cache
			var nodes = [];

			_.each(urls, function(url) {
				var node = this._getByTime(url, tm);
				if (node) {
					nodes.push(node);
				} else {
					this.remove(url);
				}
			}, this);

			return nodes;
		},

		_onData: function(/*Object[]*/data, /*Object*/options) {
			// summary: overwrite existing results with custom results
			_.each(data, function(node) {
				this.put(node);
			}, this);

			this.resultUrls = _.map(data, xml.nodeGetURL, xml);
			this._total = data.length;

			if (this.bTrackAllResults) {
				this._allResultUrls = _.clone(this.resultUrls);
			}
		},

		_onResponse: function(/*Response*/res, /*Request*/req, /*Object*/options) {
			// summary: extension point to set store data for given response
			if (!res || !res.isValid()) {
				this.resultUrls = null;
				this._allResultUrls = null;
				this._total = null;
			} else {
				// determine if total count in response is limited based on Qry.Limit.Filter parameter
				this.bQueryCountLimited = res.hasReachedCountLimit();

				if (req.getParameter(comm.OPERATION) == this.requestOperationCount) {
					// if only counting results, then just set total
					this._total = res.getTotalCount();
				} else {
					// cache crew and nodes, and update store with results and total
					_.each(res.getCrews(), function(crew) {
						csiCrews.setRolesOfCrew(crew.URL, crew.name || []);
					});

					this._setData(res.getNodes());
					this._setUrls(req.requestNodesUrls, req.requestNodesQuery, res, options);
				}
			}
		},

		_onStoreOptions: function(/*Object*/options) {
			// summary: if cachingStore specified, then use that for storage of items; if data specified, then assume no request necessary
			this._store = options.cachingStore;

			if (options.data) {
				if (this._store) {
					_.each(options.data, function(obj) {
						this.put(obj);
					}, this);
				}

				this.resultUrls = _.map(options.data, this.getIdentity, this);
			}
		},

		_resolveLimitPathUrls: function(/*Request*/request) {
			// summary: return result urls for given request; currently the only way the client can do this is
			// for a single limit path query
			var q = request.binaryQuery;

			// if client cannot evaluate query for whatever reason, then just use server to process the request
			if (q && !q.canClientPerformQuery()) {
				return null;
			}

			// if request is in any way non-standard search, then client cannot handle
			if ((request.getParameter(constants.MODULE) != constants.MODULE_SEARCH) ||
				(request.getParameter(constants.OPERATION) != constants.OPERATION_QUERY_XML) ||
				request.getParameter(constants.PRV_URL)) {
				return null;
			}

			// client does not currently support multiple limit paths, although it could
			var limitPath = request.getParameter(constants.QRY_LIMIT_PATH);
			if (!limitPath || !_.isString(limitPath)) {
				return null;
			}

			// if client does not have nodes in cache to get limit path urls, then again must use server to process request
			var obj = xml.getUrlPathObject(limitPath);
			return this._store.getLimitPathUrls && this._store.getLimitPathUrls(obj.url, obj.path, this.refreshTimestamp);
		},

		_setUrls: function(/*String[]?*/clientUrls, /*Query?*/clientQuery, /*Response?*/serverResponse, /*Object?*/options) {
			// summary: fill resultUrls and _total based on either client query from cache or server response
			var store = this._store;
			var start = options ? _getStartCount(options) : 1;
			var end = options ? _getEndCount(options) : 0;
			var resultUrls = this.resultUrls;
			var newResultUrls = serverResponse && serverResponse.getCompleteRefs();

			if (!resultUrls) {
				if (clientUrls) {
					// access control may cause fewer nodes to be returned, so only track urls for which we have nodes
					resultUrls = _getResolvedUrls(store, clientUrls);

					// apply filtering and sorting if so specified
					if (clientQuery) {
						resultUrls = _applyClientQuery(clientQuery, resultUrls, store);
					}
				} else if (serverResponse) {
					console.assert(start == 1);

					// check that response includes same number of nodes as requested, and if not, then remove from result list;
					// it is possible a node is deleted in middle of query so result list does not match result nodes
					var resultNodes = serverResponse.getResultNodes();
					if (resultNodes.length < end) {
						newResultUrls = _getResolvedUrls(store, newResultUrls.splice(0, end)).concat(newResultUrls);
					}

					resultUrls = newResultUrls;
				}
			} else if (start == (resultUrls.length + 1)) {
				// append next page of results to existing results
				resultUrls = resultUrls.concat(newResultUrls);
			}

			this.resultUrls = resultUrls;

			if (!this._total) {
				if (clientUrls) {
					// resolving limit path with known length
					this._total = resultUrls.length;
				} else if (serverResponse && serverResponse.hasTotalCount()) {
					// server returned total count, so use it
					this._total = serverResponse.getTotalCount();
				} else if (end && this.resultUrls && (end == resultUrls.length)) {
					// server returned only page of results, so cannot know total
					this._total = Infinity;
				} else {
					// server returned all results, so just get total from length
					this._total = resultUrls.length;
				}
			} else if ((this._total == Infinity) && (newResultUrls.length < (end - start + 1))) {
				// received last page of results
				this._total = resultUrls.length;
			}

			// if resultUrls were calculated on client, but user does not have access to all of them, then update based on nodes returned
			if (this._total < resultUrls.length) {
				this.resultUrls = _getResolvedUrls(store, resultUrls);
				console.assert(this.resultUrls.length == this._total);
			}

			// track when all nodes in store are cached - only do so though when ad-hoc query is empty
			// FIX: we could potentially do better here and track query used to get the results (rather than just testing for an empty one),
			// and if current query has fewer filters then we could reset _allResultUrls
			if (!this._allResultUrls && this.bTrackAllResults && !this.bQueryCountLimited && _.isEmpty(this._lastQuery) && _.isEmpty(this._lastQuickSearchQuery)) {
				if (!end || (end >= this._total)) {
					// last check - if using Fmt.Complete.Max and total has reached that, then do not record all urls
					var max = this.requestParameters && this.requestParameters["Fmt.Complete.Max"];
					if (!max || (this._total < max)) {
						this._allResultUrls = _.clone(this.resultUrls);
					}
				}
			}

			// notify of results
			this.onResultUrls(this.resultUrls);
		},

		_unload: function() {
			// summary: reset loaded members
			this.resultUrls = null;
			this._loaded = false;
			this._total = null;
		}
	});

	_Nodes.createFromRelationship = function(/*Relationship*/r, /*Context*/ctx, /*Object?*/extraStoreOptions, /*Object?*/extraSearchOptions, /*Object?*/storeClass) {
		// summary: construct store based on Relationship and optional options

		// resolve target class for relationship if not already done
		if (!r.m_rcTarget) {
			const node = ctx.getNode();
			r.resolve(null, reflection.getNodeClass(node), node, ctx.cache, true);
		}

		// Nodes data store will handle the following search options explicitly;
		// paging should be passed as query options by the widget using the store
		const searchOptions = {
			bMask: true,
			dividers: false,
			filterType: false,
			...extraSearchOptions,
		};

		// build search for Relationship
		const {dividerType, m_module, m_operation, m_rcTarget} = r;
		const search = r.getConfiguredSearch(ctx.cache, ctx.getNodeURL(), searchOptions);

		// add dependency paths - these could be overridden if passed in as extraStoreOptions.requestParameters
		search.addDependencyPaths(r.m_depPaths);

		// extract specific properties from params that exist on store - use Module and Operation directly from Relationship
		// as the Search class will apply default QueryByXML (which may override the store)
		let {Module, Operation, ...otherRequestParameters} = search.getParameters();
		let storeOptions = {};

		if (m_module) {
			storeOptions.requestModule = m_module;
		}

		if (m_operation) {
			storeOptions.requestOperation = m_operation;
		}

		if (dividerType) {
			storeOptions.dividerType = dividerType;
		}

		// apply extra store options if given - be careful to merge requestParameters
		if (extraStoreOptions) {
			const {requestParameters, ...otherStoreOptions} = extraStoreOptions;
			storeOptions = {...storeOptions, ...otherStoreOptions};
			otherRequestParameters = {...otherRequestParameters, ...requestParameters};
		}

		storeOptions.requestParameters = otherRequestParameters;
		storeOptions.sortQuery = storeOptions.sortQuery || search.getSortQuery();
		storeOptions.filterQuery = storeOptions.filterQuery || search.getQuery();

		// filter by type if class is concrete
		if (!storeOptions.typeNode && m_rcTarget && !m_rcTarget.abstract && !m_rcTarget.hasDerivedClasses()) {
			storeOptions.typeNode = m_rcTarget.getType();
			storeOptions.bTargetType = true;
		}

		storeClass = storeClass || _Nodes;
		return new storeClass(storeOptions);
	};

	/**
	 * Return empty Nodes store
	 * @return {Nodes}
	 */
	_Nodes.emptyStore = () => {
		return new _Nodes({
			data: []
		});
	}

	return _Nodes;
});